import { useTenantId } from '@/stores/tenant.store';
import { notifyDispatchUpdate } from '@/hooks/scheduler/useDispatchQueueSync';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { apiPost, operationsClient } from '@/lib/api';
import { jobEndpoints } from '@/lib/api/endpoints';
import {
  invoiceKeys,
  jobKeys,
  paymentKeys,
  paymentScheduleKeys,
  schedulerKeys,
} from '@/lib/queryKeys';
import type { Job } from '@/types/job';
import type { PipelineWorkflowOverridePayload } from '@/lib/jobs/pipelineWorkflowOverride';

export interface TransitionJobVariables extends PipelineWorkflowOverridePayload {
  id: string;
  /**
   * The stage to move the job into. `null` cancels the job without one: a
   * tenant's pipeline can leave out a Cancelled stage, and the job must still be
   * cancellable, so it goes through the lifecycle cancel endpoint instead.
   */
  stageId: string | null;
  cascadeCancelActiveAppointments?: boolean;
  closeOperation?: 'complete' | 'cancel';
}

export function useTransitionJob() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useApiMutation<Job, TransitionJobVariables>({
    mutationFn: ({
      id,
      stageId,
      cascadeCancelActiveAppointments,
      workflowOverride,
      workflowOverrideReason,
    }) =>
      stageId === null
        ? apiPost<Job, { cascadeCancelActiveAppointments?: boolean }>(
            operationsClient,
            jobEndpoints.cancel(id),
            cascadeCancelActiveAppointments !== undefined
              ? { cascadeCancelActiveAppointments }
              : {},
          )
        : apiPost<
            Job,
            {
              stageId: string;
              cascadeCancelActiveAppointments?: boolean;
              workflowOverride?: boolean;
              workflowOverrideReason?: string;
            }
          >(operationsClient, jobEndpoints.transition(id), {
            stageId,
            ...(cascadeCancelActiveAppointments !== undefined
              ? { cascadeCancelActiveAppointments }
              : {}),
            ...(workflowOverride === true ? { workflowOverride, workflowOverrideReason } : {}),
          }),
    showErrorToast: false,
    onSuccess: (data, variables) => {
      notifyDispatchUpdate(tenantId);
      void qc.invalidateQueries({ queryKey: schedulerKeys.all() });
      void qc.invalidateQueries({ queryKey: jobKeys.detail(data.id) });
      void qc.invalidateQueries({ queryKey: jobKeys.lists() });
      void qc.invalidateQueries({ queryKey: jobKeys.financialSummary(data.id) });
      void qc.invalidateQueries({ queryKey: jobKeys.payments(data.id) });
      void qc.invalidateQueries({ queryKey: invoiceKeys.byJobPrefix(data.id) });
      void qc.invalidateQueries({ queryKey: paymentKeys.lists() });
      void qc.invalidateQueries({ queryKey: paymentScheduleKeys.byJob(data.id) });
      if (variables.cascadeCancelActiveAppointments) {
        toast.success(
          `Job ${
            variables.closeOperation === 'cancel' ? 'cancelled' : 'completed'
          }. Active visits were cancelled.`,
        );
      } else {
        toast.success(variables.stageId === null ? 'Job cancelled' : 'Stage updated');
      }
    },
  });
}
